import { axios } from 'configs'
import type {
  TCurrentTerms,
  TPublishTermsVersionRequest,
  TTermsAcceptanceResult,
  TTermsDraftRequest,
  TTermsMemberDetail,
  TTermsMemberStatus,
  TTermsPolicy,
  TTermsStatus,
  TTermsVersionDetail,
  TTermsVersionListItem,
  TUpdateTermsDraftRequest,
  TUpdateTermsEnforcementRequest,
} from 'types/terms'

/** backend の TERMS_ERROR_CODES と同じ文字列（機械判定用） */
export const TERMS_ERROR_CODES = {
  acceptanceRequired: 'TERMS_ACCEPTANCE_REQUIRED',
  unavailable: 'TERMS_UNAVAILABLE',
  notPublished: 'TERMS_NOT_PUBLISHED',
  versionChanged: 'TERMS_VERSION_CHANGED',
  acceptanceNotApplicable: 'TERMS_ACCEPTANCE_NOT_APPLICABLE',
  notificationRegistrationBlocked: 'TERMS_NOTIFICATION_REGISTRATION_BLOCKED',
  policyRevisionConflict: 'TERMS_POLICY_REVISION_CONFLICT',
  draftRevisionConflict: 'TERMS_DRAFT_REVISION_CONFLICT',
  versionNotDraft: 'TERMS_VERSION_NOT_DRAFT',
} as const

type ErrorResponseLike = { status?: unknown; data?: { code?: unknown } }

const responseOf = (error: unknown): ErrorResponseLike | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined
  }
  const response: unknown = (error as { response?: unknown }).response
  return typeof response === 'object' && response !== null
    ? (response as ErrorResponseLike)
    : undefined
}

/** API エラーの機械判定用コード（`{ code }`）を取り出す */
export const getTermsErrorCode = (error: unknown): string | undefined => {
  const code = responseOf(error)?.data?.code
  return typeof code === 'string' ? code : undefined
}

/** サーバーが応答を返したエラーか（通信断・タイムアウトと区別する） */
export const hasErrorResponse = (error: unknown): boolean =>
  responseOf(error) !== undefined

/** サーバーが返した HTTP ステータス。応答が無い（通信断など）なら undefined */
export const getErrorResponseStatus = (error: unknown): number | undefined => {
  const status = responseOf(error)?.status
  return typeof status === 'number' ? status : undefined
}

// --- 顧客本人（同意前でも呼べる） ---

/**
 * @param expectedAuthUid この利用者のトークンでだけ取得する。キャッシュのキー（UID）と
 *   取得に使ったトークンの利用者を一致させ、別の利用者の状態を取り違えない
 */
export const getTermsStatus = async (
  expectedAuthUid?: string,
): Promise<TTermsStatus> => {
  const response = await axios.get<TTermsStatus>('/auth/terms-status', {
    expectedAuthUid,
  })
  return response.data
}

/** @param expectedAuthUid 表示している本人のトークンでだけ取得する */
export const getCurrentTerms = async (
  expectedAuthUid?: string,
): Promise<TCurrentTerms> => {
  const response = await axios.get<TCurrentTerms>('/terms/current', {
    expectedAuthUid,
  })
  return response.data
}

/**
 * 表示した版への本人同意。memberId・日時・世代はサーバーが決め、本文に送るのは版IDだけ。
 * @param expectedAuthUid 同意画面を表示している本人。送信時の利用者が違えばトークンを使う前に失敗させる
 */
export const acceptTerms = async (
  versionId: number,
  expectedAuthUid?: string,
): Promise<TTermsAcceptanceResult> => {
  const response = await axios.post<TTermsAcceptanceResult>(
    '/terms/acceptances',
    { versionId },
    { expectedAuthUid },
  )
  return response.data
}

// --- ADMIN ---

export const getTermsPolicy = async (): Promise<TTermsPolicy> => {
  const response = await axios.get<TTermsPolicy>('/admin/terms/policy')
  return response.data
}

export const updateTermsEnforcement = async (
  request: TUpdateTermsEnforcementRequest,
): Promise<TTermsPolicy> => {
  const response = await axios.patch<TTermsPolicy>(
    '/admin/terms/policy/enforcement',
    request,
  )
  return response.data
}

export const listTermsVersions = async (): Promise<TTermsVersionListItem[]> => {
  const response = await axios.get<TTermsVersionListItem[]>(
    '/admin/terms/versions',
  )
  return response.data
}

export const getTermsVersion = async (
  versionId: number,
): Promise<TTermsVersionDetail> => {
  const response = await axios.get<TTermsVersionDetail>(
    `/admin/terms/versions/${versionId}`,
  )
  return response.data
}

export const createTermsDraft = async (
  request: TTermsDraftRequest,
): Promise<TTermsVersionDetail> => {
  const response = await axios.post<TTermsVersionDetail>(
    '/admin/terms/versions',
    request,
  )
  return response.data
}

export const updateTermsDraft = async (
  versionId: number,
  request: TUpdateTermsDraftRequest,
): Promise<TTermsVersionDetail> => {
  const response = await axios.patch<TTermsVersionDetail>(
    `/admin/terms/versions/${versionId}`,
    request,
  )
  return response.data
}

export const publishTermsVersion = async (
  versionId: number,
  request: TPublishTermsVersionRequest,
): Promise<TTermsPolicy> => {
  const response = await axios.post<TTermsPolicy>(
    `/admin/terms/versions/${versionId}/publish`,
    request,
  )
  return response.data
}

export const listTermsMemberStatuses = async (): Promise<
  TTermsMemberStatus[]
> => {
  const response = await axios.get<TTermsMemberStatus[]>('/admin/terms/members')
  return response.data
}

export const getAccountTerms = async (
  accountId: number,
): Promise<TTermsMemberDetail> => {
  const response = await axios.get<TTermsMemberDetail>(
    `/admin/accounts/${accountId}/terms`,
  )
  return response.data
}

/** 同意必須設定を ON にする（初期版は OFF へ戻す API を持たない） */
export const enableAccountTermsRequirement = async (
  accountId: number,
): Promise<TTermsMemberDetail> => {
  const response = await axios.patch<TTermsMemberDetail>(
    `/admin/accounts/${accountId}/terms-requirement`,
    { required: true },
  )
  return response.data
}
